import { Service, PLATFORM_ID, inject } from '@angular/core';
import { BehaviorSubject, filter, lastValueFrom, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ApiService } from '@app/services/api.service';
import { FastlyChallengeService } from '@app/services/fastly-challenge.service';
import { isPlatformServer } from '@angular/common';
import Cookies from 'js-cookie';
import { hasValue } from '@app/helpers/misc';

export type KeyValue = { [key: string]: any };
export type CalcType =
  | 'bike_prime_calc'
  | 'uvg_prime_calc'
  | 'cyber_prime_calc'
  | 'household_goods_calc'
  | 'mortgage_calc'
  | null;

@Service()
export class WebformService {
  #submitting$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  #currentPage$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  #pages$: BehaviorSubject<KeyValue[]> = new BehaviorSubject<KeyValue[]>([]);
  #formData$: BehaviorSubject<KeyValue> = new BehaviorSubject<KeyValue>({});
  #calculatorResult$: BehaviorSubject<KeyValue> = new BehaviorSubject<KeyValue>({});
  #calculatorElement$: BehaviorSubject<KeyValue> = new BehaviorSubject<KeyValue>({});
  #formId: string = '';
  #shouldPersist: boolean = false;
  #hiddenFields: string[] = [];
  #submissionType: string = '';

  #platformId = inject(PLATFORM_ID);
  #http = inject(HttpClient);
  #api = inject(ApiService);
  #challenge = inject(FastlyChallengeService);

  /**
   * A valid Drupal CSRF token is a short, opaque ASCII string. When the Fastly challenge cookie
   * is missing, `/session/token` returns challenge HTML instead — which would otherwise be shoved
   * into the `X-CSRF-Token` header and crash with "Failed to construct 'Headers': non ISO-8859-1".
   */
  #isValidToken(token: string): boolean {
    return (
      typeof token === 'string' &&
      token.length > 0 &&
      token.length < 256 &&
      /^[\w.\-]+$/.test(token)
    );
  }

  #handleError(e: any) {
    console.error(e);
    document?.dispatchEvent(
      new CustomEvent('tracking:system', {
        detail: {
          message:
            e.error?.message ||
            e.message ||
            `An unknown error occurred: ${JSON.stringify(e)}`,
          type: 'error',
        },
      })
    );
  }

  set submissionType(submissionType: string) {
    this.#submissionType = submissionType;
  }

  get submissionType(): string {
    return this.#submissionType;
  }

  get formId(): string {
    return this.#formId;
  }

  set formId(value: string) {
    this.#formId = value;
  }

  get shouldPersist(): boolean {
    return this.#shouldPersist;
  }

  set shouldPersist(value: boolean) {
    this.#shouldPersist = value;
  }

  get currentPage(): Observable<number> {
    return this.#currentPage$.asObservable();
  }

  setCurrentPage(value: number) {
    this.#currentPage$.next(value);
    this.#persistData();
  }

  get submitting(): Observable<boolean> {
    return this.#submitting$.asObservable();
  }

  setSubmitting(value: boolean) {
    this.#submitting$.next(value);
  }

  get pages(): Observable<KeyValue[]> {
    return this.#pages$.asObservable();
  }

  setPages(value: KeyValue[]) {
    this.#pages$.next(value);
  }

  get formData(): Observable<KeyValue> {
    return this.#formData$.asObservable();
  }

  setFormData(value: KeyValue) {
    this.#formData$.next(value);
    this.#persistData();
  }

  getFormDataValue(field: string): unknown {
    const data = this.#formData$.value;
    if (Object.prototype.hasOwnProperty.call(data, field)) return data[field];

    return data[`${this.formId}--${field}`];
  }

  matchesValue(value: unknown, expected: string): boolean {
    if (Array.isArray(value)) {
      return value.some((entry) => this.matchesValue(entry, expected));
    }

    if (typeof value === 'object' && value !== null) {
      return Object.entries(value).some(
        ([key, entry]) =>
          (key === expected && this.isSelectedValue(entry)) ||
          this.matchesValue(entry, expected)
      );
    }

    if (typeof value === 'boolean') return (value ? '1' : '0') === expected;

    return String(value ?? '') === expected;
  }
  get calculatorResult(): Observable<KeyValue> {
    return this.#calculatorResult$.asObservable();
  }

  setCalculatorResult(value: KeyValue) {
    this.#calculatorResult$.next(value);
    this.#persistData();
  }
  get calculatorElement(): Observable<KeyValue> {
    return this.#calculatorElement$.asObservable();
  }

  setCalculatorElement(value: KeyValue) {
    this.#calculatorElement$.next(value);
    this.#persistData();
  }

  get hiddenFields(): string[] {
    return this.#hiddenFields;
  }

  addHiddenField(field: string) {
    if (!this.#hiddenFields.includes(field)) this.#hiddenFields.push(field);
  }

  removeHiddenField(field: string) {
    const index: number = this.#hiddenFields.indexOf(field);
    if (index > -1) this.#hiddenFields.splice(index, 1);
  }

  async getWebformData(
    id: string,
    params: URLSearchParams = new URLSearchParams()
  ): Promise<any> {
    try {
      const excludedParams = [
        'utm_id',
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
        'gad_source',
        'gclid',
        'gclsrc',
      ];
      const queryParams = new URLSearchParams({ _format: 'json' });
      params.forEach((value, key) => {
        if (excludedParams.includes(key.toLocaleLowerCase())) return;
        queryParams.append(key, value);
      });

      const response = await lastValueFrom(
        this.#http.get<any>(
          `${this.#api.apiUrl}/webform_rest/${id}/fields?${queryParams.toString()}`,
          {
            headers: {
              'x-requested-with': 'XMLHttpRequest',
            },
          }
        )
      );

      if (response.redirect) window.location.href = response.redirect;

      return response;
    } catch (error) {
      this.#handleError(error);
    }
  }

  async getWebformSettings(id: string): Promise<any> {
    try {
      const custom = await lastValueFrom(
        this.#http.get<any>(
          `${this.#api.apiUrl}/webform_rest/${id}/tpsettings?_format=json`
        )
      );
      const confirmation = await lastValueFrom(
        this.#http.get<any>(
          `${this.#api.apiUrl}/webform_rest/${id}/confirmation?_format=json`
        )
      );
      return {
        ...custom,
        ...confirmation,
      };
    } catch (error) {
      this.#handleError(error);
    }
  }

  async getWebformTranslations(id: string): Promise<any> {
    try {
      const custom = await lastValueFrom(
        this.#http.get<any>(`${this.#api.apiUrl}/api/webform_translations/${id}`)
      );
      return {
        ...custom,
      };
    } catch (error) {
      this.#handleError(error);
    }
  }

  async getConfirmationSettings(id: string): Promise<any> {
    try {
      const custom = await lastValueFrom(
        this.#http.get<any>(
          `${this.#api.apiUrl}/webform_rest/${id}/customconf?_format=json`
        )
      );
      return {
        ...custom,
      };
    } catch (error) {
      this.#handleError(error);
    }
  }

  async logFormError(timestamp: string, error: string, form: string): Promise<void> {
    try {
      await lastValueFrom(
        this.#http.post(
          `${this.#api.apiUrl}/form-logger`,
          {
            timestamp,
            error,
            form,
          },
          { responseType: 'text' }
        )
      );
    } catch (error) {
      this.#handleError(error);
    }
  }

  async submitWebformData(data: any, hasFiles: boolean = false): Promise<KeyValue> {
    try {
      this.setSubmitting(true);
      const token: string = await lastValueFrom(
        this.#http.get(`${this.#api.apiUrl}/session/token`, {
          responseType: 'text',
        })
      );

      // Missing/expired Fastly challenge cookie: the token endpoint returned challenge content.
      // Re-challenge (real navigation) and fail gracefully instead of crashing on the header.
      if (!this.#isValidToken(token)) {
        this.setSubmitting(false);
        this.#challenge.revalidateAndChallenge();
        throw new Error('Security check required. Please reload the page and try again.');
      }

      const filtered: any = {};

      Object.keys(data).forEach((key) => {
        if (!this.hiddenFields.includes(`${this.formId}--${key}`)) {
          filtered[key] = data[key];
        }
      });

      if (this.#calculatorElement$?.value && this.#calculatorResult$?.value) {
        if (this.#calculatorElement$.value['#webform_key']) {
          filtered[this.#calculatorElement$.value['#webform_key']] =
            this.#calculatorResult$.value;
        }
      }

      // Handle file uploads separately
      let processedData = filtered;
      if (hasFiles) processedData = await this.#processFileUploads(filtered, token);

      // Always submit as JSON to webform_rest
      const { sid }: KeyValue = await lastValueFrom(
        this.#http.post(`${this.#api.apiUrl}/webform_rest/submit`, processedData, {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': token,
          },
        })
      );

      const confirmationSettings: KeyValue = await this.getConfirmationSettings(
        this.formId
      );

      // get submission PDF
      let pdf: boolean = false;
      confirmationSettings.confirmation_steps?.forEach((step: KeyValue) => {
        if (!step.show_pdf) return;
        pdf = true;
      });

      if (pdf) confirmationSettings.pdf_blob = await this.#getSubmissionPdf(sid);

      return confirmationSettings;
    } catch (error) {
      this.setSubmitting(false);
      this.#handleError(error);
      throw error;
    }
  }

  async call(action: string): Promise<any> {
    try {
      return await lastValueFrom(this.#http.get<any>(`${this.#api.apiUrl}${action}`));
    } catch (error) {
      this.#handleError(error);
    }
  }

  async getApplication(plugin: string): Promise<string> {
    try {
      let params: string = '';

      for (const [key, value] of Object.entries(this.#formData$.value)) {
        if (key.includes('-generated_')) continue;
        let v = value;
        if (v instanceof Object) {
          const selected: string[] = [];
          const keys = Object.keys(v);
          if (typeof value[keys[0]] === 'boolean') {
            keys.forEach((key: string) => {
              if (value[key]) selected.push(key);
            });
            v = selected.join(',');
          } else {
            v = null;

            Object.entries(value).forEach(([k, val]) => {
              if (!val) return;
              params += `&${key.replace(`${this.formId}--`, '')}[${k}]=${val}`;
            });
          }
        }
        if (
          !this.hiddenFields.includes(key) &&
          hasValue(value) &&
          !key.startsWith('_generated_')
        ) {
          if (v) {
            params += `&${key.replace(`${this.formId}--`, '')}=${encodeURIComponent(v)}`;
          }
        }
      }
      params += `&webform_id=${encodeURIComponent(this.formId)}`;

      return await lastValueFrom(
        this.#http.get<any>(
          `${this.#api.apiUrl}/api/prime_generate_pdf?plugin=${plugin}${params}`,
          { responseType: 'text' as 'json' }
        )
      );
    } catch (error) {
      this.#handleError(error);
      throw error;
    }
  }

  loadPersistedData() {
    if (isPlatformServer(this.#platformId) || !this.shouldPersist || !this.formId) return;
    const cookie = Cookies.get(`ng-persist-formdata-${this.formId}`);

    if (!cookie || cookie !== 'keep') {
      this.resetPersistedData();
      return;
    }

    const store = localStorage.getItem(`ng-persist-formdata-${this.formId}`);
    if (!store) return;
    const parsed: KeyValue = JSON.parse(store);
    this.setCurrentPage(parsed.currentPage);
    this.setCalculatorResult(parsed.calculatorResult);
    this.setCalculatorElement(parsed.calculatorElement);
    this.setFormData(parsed.formData);
  }

  resetLocalStorage() {
    Cookies.remove(`ng-persist-formdata-${this.formId}`);
    localStorage.removeItem(`ng-persist-formdata-${this.formId}`);
  }

  resetPersistedData() {
    if (!this.shouldPersist) return;
    if (isPlatformServer(this.#platformId) || !this.formId) return;
    this.resetLocalStorage();

    // Set via observable and not setter to prevent empty object persistance
    this.#formData$.next({});
    this.#calculatorResult$.next({});
    this.#calculatorElement$.next({});
    this.#pages$.next([]);
    this.#currentPage$.next(0);
  }

  async #getSubmissionPdf(sid: string): Promise<string | boolean> {
    try {
      const res: any = await lastValueFrom(
        this.#http.get<any>(`${this.#api.apiUrl}/api/prime_pdf?uuid=${sid}`, {
          responseType: 'blob' as 'json',
          observe: 'response',
        })
      );

      if (res.status === 204) return false;
      return res.body;
    } catch (error) {
      this.#handleError(error);
      throw error;
    }
  }

  private isSelectedValue(value: unknown): boolean {
    return value === true || value === 1 || value === '1';
  }

  #persistData(): void {
    if (!this.shouldPersist) return;
    if (isPlatformServer(this.#platformId) || !this.formId) return;
    const state = {
      formData: this.#formData$.value,
      calculatorResult: this.#calculatorResult$.value,
      currentPage: this.#currentPage$.value,
      calculatorElement: this.#calculatorElement$.value,
    };

    Cookies.set(`ng-persist-formdata-${this.formId}`, 'keep', { expires: 7 });
    localStorage.setItem(`ng-persist-formdata-${this.formId}`, JSON.stringify(state));
  }

  async #processFileUploads(data: any, token: string): Promise<any> {
    const processedData = { ...data };

    for (const key of Object.keys(data)) {
      const value = data[key];

      // Files from mobi-ui-file-input-major are always arrays
      if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
        const fileIds: number[] = [];
        for (const file of value) {
          const fileId = await this.#uploadFileToWebform(file, key, token);
          fileIds.push(fileId);
        }

        // Check if mutli file filed
        const isMultipleField = (value as any)._isMultipleField;

        if (!isMultipleField && fileIds.length === 1) {
          processedData[key] = fileIds[0];
        } else {
          processedData[key] = fileIds;
        }
      }
    }

    return processedData;
  }

  async #uploadFileToWebform(
    file: File,
    fieldName: string,
    token: string
  ): Promise<number> {
    try {
      //Upload the file as binary data to the webform_rest endpoint
      const fileBuffer = await this.#readFileAsArrayBuffer(file);

      const response: any = await lastValueFrom(
        this.#http.post(
          `${this.#api.apiUrl}/webform_rest/${this.formId}/upload/${fieldName}?_format=json`,
          fileBuffer,
          {
            headers: {
              'Content-Type': 'application/octet-stream',
              'Content-Disposition': `file; filename="${encodeURIComponent(file.name)}"`,
              'X-CSRF-Token': token,
            },
          }
        )
      );

      return response.fid?.[0]?.value || response.fid || response.target_id;
    } catch (error) {
      console.error('Webform REST file upload failed:', error);
      throw new Error(
        `Failed to upload file ${file.name} to field ${fieldName}. Error: ${error}`
      );
    }
  }

  #readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
      reader.readAsArrayBuffer(file);
    });
  }
}
